import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { RV_PRESETS, type RvPresetId, rvSample } from '../data/rv-presets';
import { pmfExpectation, pmfStdev } from '../prob/discrete-rv';
import { createPmfChart, type PmfChartHandle, type PmfChartOptions } from '../render/pmf-chart';
import { createSvgEl } from '@learnforge/demo-render';
import { createActionBar } from '../action-bar';
import type { ProbContext, ProbKey } from '../types';
import { createToggleRow } from '@learnforge/demo-kit';

// ============================================================================
// State
// ============================================================================

interface EmpiricalState {
  count: number;
  bins: Map<number, number>;
}

interface State {
  presetId: RvPresetId;
  showVarianceBars: boolean;
  showEmpiricalBars: boolean;
  empirical: EmpiricalState;
}

// ============================================================================
// Exported helpers (tested + reused)
// ============================================================================

export function buildPmfBars(preset: (typeof RV_PRESETS)[number]) {
  return preset.pmf.map((e) => ({ x: e.value, p: e.prob }));
}

export function buildEmpiricalBars(empirical: EmpiricalState) {
  const total = empirical.count || 1;
  return [...empirical.bins.entries()].map(([x, n]) => ({ x, p: n / total }));
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.random-variables.label',
    demoId: 'probability',
    advancedKey: 'random-variables',

    initialState(): State {
      return {
        presetId: '2d6-sum',
        showVarianceBars: false,
        showEmpiricalBars: true,
        empirical: { count: 0, bins: new Map() },
      };
    },

    createRender(ctx, host, controls) {
      // ── Stage SVG ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 420', class: 'pb-rv-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);
      let chart: PmfChartHandle | null = null;

      // ── Panel: preset <select> ──
      const presetLbl = document.createElement('label');
      presetLbl.textContent = ctx.i18n.t('param.submode.label');
      const select = document.createElement('select');
      for (const p of RV_PRESETS) {
        const opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = ctx.i18n.t(p.labelKey);
        select.appendChild(opt);
      }
      select.addEventListener('change', () => {
        const newId = select.value as RvPresetId;
        controls.setState((s) => ({
          ...s,
          presetId: newId,
          empirical: { count: 0, bins: new Map() },
        }));
      });
      const presetRow = document.createElement('div');
      presetRow.className = 'pb-dropdown-row';
      presetRow.append(presetLbl, select);
      ctx.panel.appendChild(presetRow);

      // ── Panel: readout div ──
      const readoutDiv = document.createElement('div');
      readoutDiv.className = 'pb-rv-readout';
      ctx.panel.appendChild(readoutDiv);

      // ── Panel: variance-bars checkbox ──
      const varianceToggle = createToggleRow({
        labelKey: 'param.show-variance-bars.label',
        i18n: ctx.i18n,
        checked: false,
        onChange: (checked) => {
          controls.setState((s) => ({ ...s, showVarianceBars: checked }));
        },
      });
      ctx.panel.appendChild(varianceToggle.element);

      // ── Panel: empirical-bars checkbox ──
      const empiricalToggle = createToggleRow({
        labelKey: 'param.show-empirical-bars.label',
        i18n: ctx.i18n,
        checked: false,
        onChange: (checked) => {
          controls.setState((s) => ({ ...s, showEmpiricalBars: checked }));
        },
      });
      ctx.panel.appendChild(empiricalToggle.element);

      // ── Action bar ──
      const actionBarHandle = createActionBar(ctx.actionBar, [
        {
          id: 'btn.roll-one',
          labelKey: ctx.i18n.t('btn.roll-one'),
          onClick: () => {
            controls.setState((s) => {
              const preset = RV_PRESETS.find((p) => p.id === s.presetId)!;
              const bins = new Map(s.empirical.bins);
              const v = rvSample(preset, ctx.prng.next());
              bins.set(v, (bins.get(v) ?? 0) + 1);
              return { ...s, empirical: { count: s.empirical.count + 1, bins } };
            });
          },
        },
        {
          id: 'btn.roll-100',
          labelKey: ctx.i18n.t('btn.roll-100'),
          onClick: () => {
            controls.setState((s) => {
              const preset = RV_PRESETS.find((p) => p.id === s.presetId)!;
              const bins = new Map(s.empirical.bins);
              for (let i = 0; i < 100; i++) {
                const v = rvSample(preset, ctx.prng.next());
                bins.set(v, (bins.get(v) ?? 0) + 1);
              }
              return { ...s, empirical: { count: s.empirical.count + 100, bins } };
            });
          },
        },
        {
          id: 'btn.roll-1000',
          labelKey: ctx.i18n.t('btn.roll-1000'),
          onClick: () => {
            controls.setState((s) => {
              const preset = RV_PRESETS.find((p) => p.id === s.presetId)!;
              const bins = new Map(s.empirical.bins);
              for (let i = 0; i < 1000; i++) {
                const v = rvSample(preset, ctx.prng.next());
                bins.set(v, (bins.get(v) ?? 0) + 1);
              }
              return { ...s, empirical: { count: s.empirical.count + 1000, bins } };
            });
          },
        },
        {
          id: 'btn.reset',
          labelKey: ctx.i18n.t('btn.reset'),
          onClick: () => {
            controls.setState((s) => ({
              ...s,
              empirical: { count: 0, bins: new Map() },
            }));
          },
        },
      ]);

      // ── RenderHandle ──
      const update = (state: State): void => {
        const p = RV_PRESETS.find((x) => x.id === state.presetId)!;

        // Sync select
        select.value = state.presetId;

        // Sync checkboxes
        varianceToggle.setChecked(state.showVarianceBars);
        empiricalToggle.setChecked(state.showEmpiricalBars);

        // Rebuild readout innerHTML
        readoutDiv.innerHTML = `
          ${ctx.i18n.t('stat.expectation')}: ${pmfExpectation(p.pmf).toFixed(4)}<br>
          ${ctx.i18n.t('stat.stdev')}: ${pmfStdev(p.pmf).toFixed(4)}<br>
          ${
            state.empirical.count > 0
              ? `${ctx.i18n.t('stat.empirical-expectation')}: ${
                  [...state.empirical.bins.entries()].reduce((acc, [v, n]) => acc + v * n, 0) /
                  state.empirical.count
                }<br>${ctx.i18n.t('stat.sample-count')}: ${state.empirical.count}`
              : ''
          }
        `;

        // Rebuild stage chart
        chart?.destroy();
        stageGroup.replaceChildren();

        const values = p.pmf.map((e) => e.value);
        const xMin = Math.min(...values) - 0.5;
        const xMax = Math.max(...values) + 0.5;

        const opts: PmfChartOptions & {
          empiricalBars?: typeof undefined;
          markers?: typeof undefined;
        } = {
          xDomain: [xMin, xMax],
          bars: buildPmfBars(p),
          xLabelKey: 'axis.value',
          yLabelKey: 'axis.probability',
          i18n: ctx.i18n,
        };

        if (state.showEmpiricalBars) {
          (opts as PmfChartOptions).empiricalBars = buildEmpiricalBars(state.empirical);
        }

        if (state.showVarianceBars) {
          (opts as PmfChartOptions).markers = [
            { x: pmfExpectation(p.pmf), labelKey: 'stat.expectation' },
            { x: pmfExpectation(p.pmf) + pmfStdev(p.pmf), labelKey: 'stat.stdev' },
            { x: pmfExpectation(p.pmf) - pmfStdev(p.pmf), labelKey: 'stat.stdev' },
          ];
        }

        chart = createPmfChart(stageGroup, opts as unknown as PmfChartOptions);
      };

      return {
        update,
        dispose() {
          chart?.destroy();
          actionBarHandle.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

/**
 * Alias for backward compatibility with index.ts and tests that import
 * `mountRandomVariables` by name.
 */
export const mountRandomVariables = experiment.mount.bind(experiment);
